/**
 * System execution order — per MECHANICS.md §1.1.
 *
 * The tick function runs systems in the fixed order below. New systems
 * are added by editing the SYSTEMS array; ordering decisions must be
 * explicit and reviewed — no dynamic registration.
 *
 * A SystemFn takes the current state + the commands emitted this tick
 * + the sim's RNG, mutates state (in place — the tick function owns
 * the mutability envelope), and returns the next state. RNG must be
 * threaded explicitly; global randomness is forbidden per the
 * MECHANICS.md determinism rules.
 */

import type { Command } from './commands';
import type { GameState } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';

// System stubs — v0M2.3 empty stubs that return state unchanged. Real
// implementations land per-milestone as consuming code arrives.
// `apply-commands` and `movement` are live at v0M3.1.
import { applyCommands } from './apply-commands';
import { movementSystem } from './movement';
import { zocDetectionSystem } from './zoc-detection';
import { battlesSystem } from './battles';
import { siegesSystem } from './sieges';
import { pillagesSystem } from './pillages';
import { repairSystem } from './repair';
import { economySystem } from './economy';
import { researchSystem } from './research';
import { supplySystem } from './supply';
import { aiSystem } from './ai';
import { visibilitySystem } from './visibility';
import { victorySystem } from './victory';
import { espionageSystem } from './espionage';
import { buildSystem, captureSystem } from './build';

export type SystemFn = (
  state: GameState,
  commands: readonly Command[],
  rng: RngState,
  context: SimContext,
) => GameState;

/**
 * Fixed system execution order per MECHANICS.md §1.1. Do not reorder
 * without cross-referencing the doc — the ordering is load-bearing for
 * determinism and for downstream feature contracts (e.g. movement must
 * precede ZoC detection so engagement triggers see the updated positions).
 */
export const SYSTEMS: readonly { readonly name: string; readonly run: SystemFn }[] = [
  { name: 'apply-commands', run: applyCommands },
  { name: 'movement', run: movementSystem },
  // v0M7.3 (2026-07-25) — build runs after movement (needs updated
  // Army positions to check the arrival-epsilon gate) and before
  // zoc-detection / battles / visibility so completed WorldStructures
  // are visible to those systems this same tick.
  { name: 'build', run: buildSystem },
  { name: 'zoc-detection', run: zocDetectionSystem },
  { name: 'battles', run: battlesSystem },
  { name: 'sieges', run: siegesSystem },
  // v0M9 (2026-07-27) — pillages runs after sieges (so completed siege
  // outcomes are visible) and before economy (so this-tick pillage
  // completion halts structure income this tick). Applies only to
  // minor Settlements per MECHANICS.md §2 v0M9.
  { name: 'pillages', run: pillagesSystem },
  // v0M9 (2026-07-27) — repair runs after pillages so an enemy who
  // opened a pillage timer this tick freezes the repair timer this
  // same tick. Pauses on hostile-in-ZoC. Applies only to minor
  // Settlements with an active repairState.
  { name: 'repair', run: repairSystem },
  { name: 'economy', run: economySystem },
  // v0M9.4 (2026-07-24) — research runs after economy so learning produced
  // this tick is banked before completion checks. No RNG; faction-scoped.
  { name: 'research', run: researchSystem },
  { name: 'supply', run: supplySystem },
  { name: 'ai', run: aiSystem },
  // v0M5.4 (2026-07-25) — espionage advances infiltration counters +
  // fires Counter-Espionage kill rolls at day-boundary ticks. Runs
  // before visibility so a spy killed this tick doesn't contribute
  // vision this tick's frame.
  { name: 'espionage', run: espionageSystem },
  // v0M7.3 (2026-07-25) — capture ticks at the day boundary. Runs
  // after espionage but before visibility so an ownership flip this
  // tick propagates into the visibility layer next tick.
  { name: 'capture', run: captureSystem },
  { name: 'visibility', run: visibilitySystem },
  { name: 'victory', run: victorySystem },
];

/** Names of the systems in execution order. Used by tests to assert the order. */
export function systemNames(): readonly string[] {
  return SYSTEMS.map((s) => s.name);
}
